import { type ReactNode, useEffect, useReducer } from 'react'
import { useLocation } from 'react-router'
import { hasHotelPortalSession, setHotelPortalUnauthorizedHandler } from '@/gateway/client'
import { AuthHeader } from '@/screens/auth/components/AuthHeader'
import { AuthLayout } from '@/screens/auth/components/AuthLayout'

/** Navigation state the portal layout sets after a deliberate log out. */
export interface HotelPortalRouteState {
  readonly signedOut?: boolean
}

/**
 * Route gate for the hotel-facing Secure Hotel Portal — the counterpart of
 * `ProtectedRoute` for a different audience and a different session.
 *
 * There is no portal sign-in page to redirect to: a hotel signs in only
 * through its emailed link, whose token this route does not have. So a missing
 * or rejected session shows how to get back in instead. Client-side gate for
 * UX only — every portal endpoint checks the session itself.
 *
 * The session is re-read from the gateway on every render rather than held in
 * state, so a log out (which navigates here) and a 401 (which calls the
 * handler below) both close the portal at once.
 */
export function HotelPortalRoute({ children }: { readonly children: ReactNode }) {
  const location = useLocation()
  const [, rerender] = useReducer((count: number) => count + 1, 0)

  useEffect(() => {
    // A 401 on any portal request has already cleared the session (client.ts).
    setHotelPortalUnauthorizedHandler(rerender)
    return () => setHotelPortalUnauthorizedHandler(null)
  }, [])

  if (!hasHotelPortalSession()) {
    const state: unknown = location.state
    const signedOut =
      typeof state === 'object' &&
      state !== null &&
      'signedOut' in state &&
      state.signedOut === true
    return (
      <AuthLayout>
        <AuthHeader
          title={signedOut ? 'Signed Out' : 'Session Ended'}
          description={
            signedOut
              ? 'You have signed out of the Secure Hotel Portal. Open the secure link in your email to sign back in.'
              : 'Your secure session has ended. Open the secure link in your email again to sign back in.'
          }
        />
      </AuthLayout>
    )
  }
  return children
}
